
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useUserStore } from "@/store/userStore";
import { useAuth } from "@/contexts/AuthContext";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, setRole } = useUserStore();
  const { user, loading } = useAuth();
  const [isHandlingRefresh, setIsHandlingRefresh] = useState(true);

  useEffect(() => {
    console.log("NotFound: Current state:", { 
      role, 
      isAuthenticated: !!user,
      pathname: location.pathname,
      loading,
    });

    // Wait for auth state to be determined
    if (loading) {
      console.log("NotFound: Still loading auth state...");
      return;
    }

    // If user is not authenticated and not on the login or root page, redirect to login
    if (!user && location.pathname !== '/login' && location.pathname !== '/') {
      console.log("NotFound: User is not authenticated, redirecting to login");
      navigate("/login", { replace: true });
      return;
    }

    // Handle potential refresh scenarios for authenticated users
    if (user) {
      console.log("NotFound: User is authenticated, checking path and role");
      
      // Check if we're on the root path and need to redirect based on role
      if (location.pathname === '/' || location.pathname === '') {
        if (role === 'admin') {
          console.log("NotFound: Root path with admin role, redirecting to admin dashboard");
          navigate("/admin/dashboard", { replace: true });
          return;
        } else if (role === 'coach') {
          console.log("NotFound: Root path with coach role, redirecting to coach schedule");
          navigate("/coach/schedule", { replace: true });
          return;
        } else if (role === 'athlete') {
          console.log("NotFound: Root path with athlete role, redirecting to athlete dashboard");
          navigate("/athlete/dashboard", { replace: true });
          return;
        }
      }
      
      // Determine where to redirect based on role if we're on a 404
      if (location.pathname.includes('/404') || location.pathname === '*') {
        if (role === 'admin') {
          console.log("NotFound: 404 with admin role, redirecting to admin dashboard");
          navigate("/admin/dashboard", { replace: true });
          return;
        } else if (role === 'coach') {
          console.log("NotFound: 404 with coach role, redirecting to coach schedule");
          navigate("/coach/schedule", { replace: true });
          return;
        } else if (role === 'athlete') {
          console.log("NotFound: 404 with athlete role, redirecting to athlete dashboard");
          navigate("/athlete/dashboard", { replace: true });
          return;
        }
      }
      
      // If we can't determine from path, but user is logged in and no role is set
      if (!role) {
        console.log("NotFound: User logged in but role not set, defaulting to athlete");
        setRole('athlete');
        navigate("/athlete/dashboard", { replace: true });
        return;
      }
    }
    
    setIsHandlingRefresh(false);
  }, [location.pathname, navigate, role, user, loading, setRole]);

  const handleHomeClick = () => {
    // If not logged in, go to login page
    if (!user) {
      navigate("/login");
      return;
    }
    
    // Redirect to the appropriate home based on user role
    if (role === 'admin') {
      navigate("/admin/dashboard");
    } else if (role === 'coach') {
      navigate("/coach/schedule");
    } else if (role === 'athlete') {
      navigate("/athlete/dashboard");
    } else {
      navigate("/");
    }
  };

  // Don't show 404 page while we're trying to handle a potential refresh
  if (isHandlingRefresh || loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <p>Loading...</p>
    </div>;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center">
        <h1 className="text-4xl font-bold mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-4">Oops! Page not found</p>
        <Button 
          variant="default" 
          className="bg-amber-500 hover:bg-amber-600"
          onClick={handleHomeClick}
        >
          {!user ? "Go to Login" : "Return to Home"}
        </Button>
      </div>
    </div>
  );
};

export default NotFound;
